import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, FileArchive, FolderOpen, Globe, Link2, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Toast } from '../../../components/design-system/common/Toast';
import { PageContainer } from '../../../common/components/PageContainer';
import { useVendorsApi } from '../../../common/hooks/use-vendors-api.hook';
import {
  DemoSite, DemoUpload, DemoVisitSummary, LinkMode, SlugStatus, apiErrorMessage, useDemoSitesApi,
} from '../../../common/hooks/use-demo-sites-api.hook';
import { QualityBadge, QualityReportDialog } from './demo-quality';
import { DemoVisitsSheet, VisitsCell } from './demo-visits';
import { withTeamTag } from '../../../common/services/preview-tag.service';

/** How often the list is refreshed while a demo is being checked (after a generate). */
const PENDING_REFRESH_MS = 20_000;

const DOMAIN = 'ardealy.link';

type VendorOption = { _id: string; name: string; alias?: string; kind?: 'customer' | 'prospect' };

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** A vendor's alias turned into something that can be a subdomain. */
const suggestSlug = (vendor?: VendorOption): string =>
  (vendor?.alias || vendor?.name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

const uploadSize = (upload: DemoUpload | null): number =>
  !upload ? 0 : upload.kind === 'zip' ? upload.file.size : upload.files.reduce((n, f) => n + f.size, 0);

/** A full http(s) address, which is all the API accepts as a link target. */
const isLinkable = (value: string): boolean => {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

const hostOf = (value: string): string => {
  try {
    return new URL(value).host;
  } catch {
    return value;
  }
};

/**
 * Super-admin page for vendor demo websites. Each one lives at
 * <slug>.ardealy.link: publishing uploads a zip or a folder, which the API
 * stores and a Cloudflare Worker serves for that subdomain.
 */
export const DemoSitesPage: React.FC = () => {
  const { t } = useTranslation();
  const api = useDemoSitesApi();
  const vendorsApi = useVendorsApi();

  const [sites, setSites] = useState<DemoSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  // Publish dialog. `editing` is the site being republished (its vendor and address are fixed).
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DemoSite | null>(null);
  const [vendorId, setVendorId] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<'site' | 'link'>('site');
  const [targetUrl, setTargetUrl] = useState('');
  const [linkMode, setLinkMode] = useState<LinkMode>('redirect');
  const [injectSdk, setInjectSdk] = useState(true);
  const [upload, setUpload] = useState<DemoUpload | null>(null);
  const [slugStatus, setSlugStatus] = useState<SlugStatus | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [unpublishing, setUnpublishing] = useState<DemoSite | null>(null);
  const [removing, setRemoving] = useState(false);

  // Quality: the demo whose report is open, and the demos being checked now.
  const [reportSlug, setReportSlug] = useState<string | null>(null);
  const [checking, setChecking] = useState<Set<string>>(new Set());
  const [savingExpiry, setSavingExpiry] = useState<string | null>(null);
  const reportSite = useMemo(() => sites.find((x) => x.slug === reportSlug) || null, [sites, reportSlug]);

  // Visits: each demo's numbers for the list, and the demo whose visits are open.
  const [visitSummary, setVisitSummary] = useState<Record<string, DemoVisitSummary>>({});
  // ?visits=<slug> opens a demo's visits: the link in the "demo opened" alert and on the CRM lead.
  const [searchParams, setSearchParams] = useSearchParams();
  const visitsSlug = searchParams.get('visits');
  const setVisitsSlug = (slug: string | null) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (slug) next.set('visits', slug);
      else next.delete('visits');
      return next;
    });
  const [refreshingTrackers, setRefreshingTrackers] = useState(false);
  const visitsSite = useMemo(() => sites.find((x) => x.slug === visitsSlug) || null, [sites, visitsSlug]);

  const zipInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  // `webkitdirectory` is not in React's input typings; set it on the element.
  useEffect(() => {
    folderInput.current?.setAttribute('webkitdirectory', '');
    folderInput.current?.setAttribute('directory', '');
  }, [dialogOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSites((await api.list()) || []);
      // The numbers are a nice-to-have: the list shows without them.
      api.visitSummary().then((v) => setVisitSummary(v || {})).catch(() => undefined);
    } catch {
      setToast({ msg: t('SUPER_ADMIN.DEMO_SITES.LOAD_FAILED'), sev: 'error' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** After a tracker change: every demo gets the current tracker and analytics tag. */
  const refreshTrackers = async () => {
    setRefreshingTrackers(true);
    try {
      const res = await api.enableTrackingEverywhere();
      setToast({ msg: t('SUPER_ADMIN.DEMO_SITES.VISITS.REFRESHED_ALL', { updated: res.updated, sites: res.sites }), sev: 'success' });
      load();
    } catch {
      setToast({ msg: t('SUPER_ADMIN.DEMO_SITES.VISITS.REFRESH_ALL_FAILED'), sev: 'error' });
    } finally {
      setRefreshingTrackers(false);
    }
  };

  useEffect(() => {
    load();
    // A demo can be for a customer or for a prospect (a vendor made for a demo).
    Promise.all([
      vendorsApi.findAllVendors(1, 1000),
      vendorsApi.findAllVendors(1, 1000, undefined, 'prospect').catch(() => ({ data: [] })),
    ])
      .then(([customers, prospects]: any[]) => setVendors(
        [...(customers?.data || []), ...(prospects?.data || []).map((v: VendorOption) => ({ ...v, kind: 'prospect' as const }))]
          .sort((a: VendorOption, b: VendorOption) => a.name.localeCompare(b.name)),
      ))
      .catch(() => setVendors([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While a demo is waiting for its check (it runs on its own after a generate), the list refreshes itself.
  const anyPending = sites.some((x) => x.quality?.status === 'pending');
  useEffect(() => {
    if (!anyPending) return undefined;
    const timer = setInterval(() => {
      api.list().then((next) => setSites(next || [])).catch(() => undefined);
    }, PENDING_REFRESH_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyPending]);

  const recheck = async (site: DemoSite) => {
    setChecking((prev) => new Set(prev).add(site.slug));
    try {
      const quality = await api.checkQuality(site.slug);
      setSites((prev) => prev.map((x) => (x.slug === site.slug ? { ...x, quality } : x)));
    } catch (e) {
      setToast({ msg: (await apiErrorMessage(e)) || t('SUPER_ADMIN.DEMO_SITES.QUALITY.CHECK_FAILED'), sev: 'error' });
    } finally {
      setChecking((prev) => {
        const next = new Set(prev);
        next.delete(site.slug);
        return next;
      });
    }
  };

  const selectedVendor = useMemo(() => vendors.find((v) => v._id === vendorId), [vendors, vendorId]);

  // Suggest the address from the vendor until it is typed by hand.
  useEffect(() => {
    if (!editing && !slugTouched) setSlug(suggestSlug(selectedVendor));
  }, [selectedVendor, editing, slugTouched]);

  // Check the address as it is typed.
  useEffect(() => {
    if (!dialogOpen || editing || !slug) {
      setSlugStatus(null);
      return undefined;
    }
    setCheckingSlug(true);
    const timer = setTimeout(async () => {
      try {
        setSlugStatus(await api.slugStatus(slug));
      } catch {
        setSlugStatus(null);
      } finally {
        setCheckingSlug(false);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, dialogOpen, editing]);

  const openNew = () => {
    setEditing(null);
    setVendorId('');
    setSlug('');
    setSlugTouched(false);
    setTitle('');
    setKind('site');
    setTargetUrl('');
    setLinkMode('redirect');
    setInjectSdk(true);
    setUpload(null);
    setDialogOpen(true);
  };

  const openUpdate = (site: DemoSite) => {
    setEditing(site);
    setVendorId(site.vendor?._id || '');
    setSlug(site.slug);
    setTitle(site.title || '');
    setKind(site.kind || 'site');
    setTargetUrl(site.targetUrl || '');
    setLinkMode(site.linkMode || 'redirect');
    setInjectSdk(site.kind === 'link' ? true : site.injectSdk);
    setUpload(null);
    setDialogOpen(true);
  };

  const onZipChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setUpload({ kind: 'zip', file });
    e.target.value = '';
  };

  const onFolderChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setUpload({ kind: 'folder', files, paths: files.map((f) => (f as any).webkitRelativePath || f.name) });
    }
    e.target.value = '';
  };

  const slugTakenByOther = !!slugStatus?.taken && slugStatus.takenByVendorId !== vendorId;
  const hasContent = kind === 'site' ? !!upload : isLinkable(targetUrl);
  const canPublish = !!vendorId && !!slug && hasContent && !publishing
    && (!!editing || (!!slugStatus?.valid && !slugTakenByOther && !checkingSlug));

  const publish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    try {
      const result = await api.publish(kind === 'link'
        ? { kind, slug, vendorId, title, targetUrl: targetUrl.trim(), linkMode }
        : { kind, slug, vendorId, title, injectSdk, upload: upload as DemoUpload });
      setDialogOpen(false);
      setToast({ msg: t('SUPER_ADMIN.DEMO_SITES.PUBLISHED', { url: result.url }), sev: 'success' });
      load();
    } catch (e) {
      setToast({ msg: (await apiErrorMessage(e)) || t('SUPER_ADMIN.DEMO_SITES.PUBLISH_FAILED'), sev: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  /** A generated demo comes down by itself; keep it 45 more days, or for good. */
  const changeExpiry = async (site: DemoSite, days: number | null) => {
    setSavingExpiry(site.slug);
    try {
      const { expiresAt } = await api.setExpiry(site.slug, days);
      setSites((prev) => prev.map((x) => (x.slug === site.slug ? { ...x, expiresAt } : x)));
      setToast({
        msg: expiresAt
          ? t('SUPER_ADMIN.DEMO_SITES.EXPIRY_EXTENDED', { url: site.url, date: new Date(expiresAt).toLocaleDateString() })
          : t('SUPER_ADMIN.DEMO_SITES.EXPIRY_KEPT', { url: site.url }),
        sev: 'success',
      });
    } catch (e) {
      setToast({ msg: (await apiErrorMessage(e)) || t('SUPER_ADMIN.DEMO_SITES.EXPIRY_FAILED'), sev: 'error' });
    } finally {
      setSavingExpiry(null);
    }
  };

  const confirmUnpublish = async () => {
    if (!unpublishing) return;
    setRemoving(true);
    try {
      await api.unpublish(unpublishing.slug);
      setToast({ msg: t('SUPER_ADMIN.DEMO_SITES.UNPUBLISHED', { url: unpublishing.url }), sev: 'success' });
      setUnpublishing(null);
      load();
    } catch (e) {
      setToast({ msg: (await apiErrorMessage(e)) || t('SUPER_ADMIN.DEMO_SITES.UNPUBLISH_FAILED'), sev: 'error' });
    } finally {
      setRemoving(false);
    }
  };

  const slugHint = (() => {
    if (editing || !slug) return null;
    if (checkingSlug) return { text: t('SUPER_ADMIN.DEMO_SITES.SLUG_CHECKING'), tone: 'muted' };
    if (!slugStatus) return null;
    if (!slugStatus.valid) return { text: slugStatus.problem, tone: 'error' };
    if (slugTakenByOther) return { text: t('SUPER_ADMIN.DEMO_SITES.SLUG_TAKEN', { vendor: slugStatus.takenBy }), tone: 'error' };
    if (slugStatus.taken) return { text: t('SUPER_ADMIN.DEMO_SITES.SLUG_OWN'), tone: 'muted' };
    return { text: t('SUPER_ADMIN.DEMO_SITES.SLUG_FREE'), tone: 'success' };
  })();

  return (
    <PageContainer breadcrumbs={[{ label: t('SUPER_ADMIN.DEMO_SITES.TITLE') }]}>
      <div>
        <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center md:mb-8">
          <div>
            <h1 className="mb-1 font-heading text-2xl font-medium tracking-tight">{t('SUPER_ADMIN.DEMO_SITES.TITLE')}</h1>
            <p className="text-sm text-muted-foreground">{t('SUPER_ADMIN.DEMO_SITES.SUBTITLE')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="lg" onClick={refreshTrackers} disabled={refreshingTrackers} data-testid="demo-trackers-refresh">
              <RefreshCw data-icon="inline-start" />
              {refreshingTrackers ? t('SUPER_ADMIN.DEMO_SITES.VISITS.REFRESHING_ALL') : t('SUPER_ADMIN.DEMO_SITES.VISITS.REFRESH_ALL')}
            </Button>
            <Button variant="outline" size="lg" onClick={openNew}>
              <Plus data-icon="inline-start" />
              {t('SUPER_ADMIN.DEMO_SITES.NEW')}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : sites.length === 0 ? (
          <Card className="py-4">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon"><Globe /></EmptyMedia>
                <EmptyTitle>{t('SUPER_ADMIN.DEMO_SITES.EMPTY_TITLE')}</EmptyTitle>
                <EmptyDescription>{t('SUPER_ADMIN.DEMO_SITES.EMPTY_BODY')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          </Card>
        ) : (
          <Card className="py-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="ps-4">{t('SUPER_ADMIN.DEMO_SITES.COL_SITE')}</TableHead>
                  <TableHead>{t('SUPER_ADMIN.DEMO_SITES.QUALITY.COL')}</TableHead>
                  <TableHead>{t('SUPER_ADMIN.DEMO_SITES.VISITS.COL')}</TableHead>
                  <TableHead>{t('SUPER_ADMIN.DEMO_SITES.COL_VENDOR')}</TableHead>
                  <TableHead>{t('SUPER_ADMIN.DEMO_SITES.COL_BOOKINGS')}</TableHead>
                  {/* Size is secondary: it gives its room to the visits on narrower screens. */}
                  <TableHead className="hidden 2xl:table-cell">{t('SUPER_ADMIN.DEMO_SITES.COL_FILES')}</TableHead>
                  <TableHead>{t('SUPER_ADMIN.DEMO_SITES.COL_PUBLISHED')}</TableHead>
                  <TableHead className="pe-4 text-end" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sites.map((site) => (
                  <TableRow key={site.slug}>
                    <TableCell className="ps-4">
                      {/* Opened from here, the visit is ours: tagged so the demo never counts it. */}
                      <a href={withTeamTag(site.url)} target="_blank" rel="noreferrer" className="font-medium text-foreground hover:underline">
                        {site.slug}.{DOMAIN}
                      </a>
                      {site.title && <div className="text-xs text-muted-foreground">{site.title}</div>}
                      {site.sourceUrl && (
                        <a href={site.sourceUrl} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:underline">
                          {t('SUPER_ADMIN.DEMO_SITES.FROM_PAGE', { host: hostOf(site.sourceUrl) })}
                        </a>
                      )}
                    </TableCell>
                    <TableCell>
                      {site.kind === 'link' ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <QualityBadge quality={site.quality} checking={checking.has(site.slug)} onOpen={() => setReportSlug(site.slug)} />
                      )}
                    </TableCell>
                    <TableCell>
                      <VisitsCell site={site} summary={visitSummary[site.slug]} onOpen={() => setVisitsSlug(site.slug)} />
                    </TableCell>
                    <TableCell>{site.vendor?.name || '—'}</TableCell>
                    {/* Calls booked from the demo's bar; the requests themselves are on Signups → Demo requests. */}
                    <TableCell className="text-muted-foreground" data-testid={`demo-bookings-${site.slug}`}>
                      {site.bookings?.count ? (
                        <>
                          <Link to="/signups?tab=demos" className="font-medium text-foreground hover:underline">
                            {site.bookings.count}
                          </Link>
                          {site.bookings.lastAt && (
                            <div className="text-xs">{t('SUPER_ADMIN.DEMO_SITES.BOOKINGS_LAST', { date: new Date(site.bookings.lastAt).toLocaleDateString() })}</div>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="hidden text-muted-foreground 2xl:table-cell">
                      {site.kind === 'link' ? (
                        <span className="inline-flex items-center gap-1.5" title={site.targetUrl}>
                          <Link2 className="size-3.5" />
                          {t(site.linkMode === 'embed' ? 'SUPER_ADMIN.DEMO_SITES.LINK_SHOWS' : 'SUPER_ADMIN.DEMO_SITES.LINK_REDIRECTS', { host: hostOf(site.targetUrl) })}
                        </span>
                      ) : (
                        t('SUPER_ADMIN.DEMO_SITES.FILES_SUMMARY', { count: site.fileCount, size: formatSize(site.totalBytes) })
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {site.publishedAt ? new Date(site.publishedAt).toLocaleString() : '—'}
                      {site.publishedBy && (
                        <div className="text-xs">{t('SUPER_ADMIN.DEMO_SITES.PUBLISHED_BY', { email: site.publishedBy })}</div>
                      )}
                      {site.expiresAt && (
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs">
                          <span>{t('SUPER_ADMIN.DEMO_SITES.EXPIRES', { date: new Date(site.expiresAt).toLocaleDateString() })}</span>
                          <button
                            type="button"
                            className="text-foreground underline-offset-2 hover:underline disabled:opacity-50"
                            disabled={savingExpiry === site.slug}
                            onClick={() => changeExpiry(site, 45)}
                          >
                            {t('SUPER_ADMIN.DEMO_SITES.EXTEND')}
                          </button>
                          <button
                            type="button"
                            className="text-foreground underline-offset-2 hover:underline disabled:opacity-50"
                            disabled={savingExpiry === site.slug}
                            onClick={() => changeExpiry(site, null)}
                          >
                            {t('SUPER_ADMIN.DEMO_SITES.KEEP')}
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="pe-4">
                      <div className="flex justify-end gap-1.5">
                        <Button variant="outline" size="sm" asChild>
                          <a href={withTeamTag(site.url)} target="_blank" rel="noreferrer">
                            <ExternalLink data-icon="inline-start" />
                            {t('SUPER_ADMIN.DEMO_SITES.OPEN')}
                          </a>
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openUpdate(site)}>
                          <RefreshCw data-icon="inline-start" />
                          {t('SUPER_ADMIN.DEMO_SITES.UPDATE')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setUnpublishing(site)}
                        >
                          <Trash2 data-icon="inline-start" />
                          {t('SUPER_ADMIN.DEMO_SITES.UNPUBLISH')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <DemoVisitsSheet site={visitsSite} onClose={() => setVisitsSlug(null)} onTrackingEnabled={load} />

      <QualityReportDialog
        site={reportSite}
        checking={!!reportSite && checking.has(reportSite.slug)}
        onRecheck={() => reportSite && recheck(reportSite)}
        onClose={() => setReportSlug(null)}
      />

      {/* Publish / republish */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!publishing) setDialogOpen(o); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t('SUPER_ADMIN.DEMO_SITES.DIALOG_UPDATE_TITLE', { url: `${editing.slug}.${DOMAIN}` })
                : t('SUPER_ADMIN.DEMO_SITES.DIALOG_NEW_TITLE')}
            </DialogTitle>
            <DialogDescription>
              {t(kind === 'link' ? 'SUPER_ADMIN.DEMO_SITES.DIALOG_DESCRIPTION_LINK' : 'SUPER_ADMIN.DEMO_SITES.DIALOG_DESCRIPTION')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <Tabs value={kind} onValueChange={(v) => setKind(v as 'site' | 'link')}>
              <TabsList className="w-full">
                <TabsTrigger value="site" className="flex-1">
                  <FileArchive />
                  {t('SUPER_ADMIN.DEMO_SITES.KIND_SITE')}
                </TabsTrigger>
                <TabsTrigger value="link" className="flex-1">
                  <Link2 />
                  {t('SUPER_ADMIN.DEMO_SITES.KIND_LINK')}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Field>
              <FieldLabel htmlFor="demo-vendor">{t('SUPER_ADMIN.DEMO_SITES.VENDOR')}</FieldLabel>
              <NativeSelect
                id="demo-vendor"
                className="w-full"
                value={vendorId}
                disabled={!!editing}
                onChange={(e) => setVendorId(e.target.value)}
              >
                <NativeSelectOption value="">{t('SUPER_ADMIN.DEMO_SITES.VENDOR_PLACEHOLDER')}</NativeSelectOption>
                {vendors.map((v) => (
                  <NativeSelectOption key={v._id} value={v._id}>
                    {v.kind === 'prospect' ? t('SUPER_ADMIN.DEMO_SITES.VENDOR_PROSPECT', { name: v.name }) : v.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>

            <Field>
              <FieldLabel htmlFor="demo-slug">{t('SUPER_ADMIN.DEMO_SITES.SUBDOMAIN')}</FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  id="demo-slug"
                  value={slug}
                  disabled={!!editing}
                  onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase().trim()); }}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">.{DOMAIN}</span>
              </div>
              {slugHint && (
                <FieldDescription
                  className={cn(
                    slugHint.tone === 'error' && 'text-destructive',
                    slugHint.tone === 'success' && 'text-success',
                  )}
                >
                  {slugHint.text}
                </FieldDescription>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="demo-title">{t('SUPER_ADMIN.DEMO_SITES.SITE_TITLE')}</FieldLabel>
              <Input
                id="demo-title"
                value={title}
                placeholder={t('SUPER_ADMIN.DEMO_SITES.SITE_TITLE_PLACEHOLDER')}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>

            {kind === 'site' ? (
            <>
            <Field>
              <FieldLabel>{t('SUPER_ADMIN.DEMO_SITES.CONTENT')}</FieldLabel>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => zipInput.current?.click()}>
                  <FileArchive data-icon="inline-start" />
                  {t('SUPER_ADMIN.DEMO_SITES.CHOOSE_ZIP')}
                </Button>
                <Button type="button" variant="outline" onClick={() => folderInput.current?.click()}>
                  <FolderOpen data-icon="inline-start" />
                  {t('SUPER_ADMIN.DEMO_SITES.CHOOSE_FOLDER')}
                </Button>
              </div>
              <input ref={zipInput} type="file" accept=".zip,application/zip" hidden onChange={onZipChosen} />
              <input ref={folderInput} type="file" multiple hidden onChange={onFolderChosen} />
              {upload && (
                <FieldDescription>
                  {upload.kind === 'zip'
                    ? t('SUPER_ADMIN.DEMO_SITES.SELECTED_ZIP', { name: upload.file.name, size: formatSize(uploadSize(upload)) })
                    : t('SUPER_ADMIN.DEMO_SITES.SELECTED_FOLDER', { count: upload.files.length, size: formatSize(uploadSize(upload)) })}
                </FieldDescription>
              )}
            </Field>

            <Field orientation="horizontal">
              <Switch id="demo-inject" checked={injectSdk} onCheckedChange={setInjectSdk} />
              <div>
                <FieldLabel htmlFor="demo-inject">{t('SUPER_ADMIN.DEMO_SITES.INJECT_SDK')}</FieldLabel>
                <FieldDescription>{t('SUPER_ADMIN.DEMO_SITES.INJECT_SDK_HINT')}</FieldDescription>
              </div>
            </Field>
            </>
            ) : (
            <>
            <Field>
              <FieldLabel htmlFor="demo-target">{t('SUPER_ADMIN.DEMO_SITES.LINK_URL')}</FieldLabel>
              <Input
                id="demo-target"
                type="url"
                value={targetUrl}
                placeholder="https://"
                aria-invalid={!!targetUrl && !isLinkable(targetUrl)}
                onChange={(e) => setTargetUrl(e.target.value)}
              />
              <FieldDescription className={cn(!!targetUrl && !isLinkable(targetUrl) && 'text-destructive')}>
                {targetUrl && !isLinkable(targetUrl)
                  ? t('SUPER_ADMIN.DEMO_SITES.LINK_URL_INVALID')
                  : t('SUPER_ADMIN.DEMO_SITES.LINK_URL_HINT')}
              </FieldDescription>
            </Field>

            <Field>
              <FieldLabel>{t('SUPER_ADMIN.DEMO_SITES.LINK_MODE')}</FieldLabel>
              <RadioGroup value={linkMode} onValueChange={(v) => setLinkMode(v as LinkMode)} className="gap-3">
                {(['redirect', 'embed'] as const).map((mode) => (
                  <label key={mode} htmlFor={`demo-mode-${mode}`} className="flex cursor-pointer items-start gap-3">
                    <RadioGroupItem id={`demo-mode-${mode}`} value={mode} className="mt-0.5" />
                    <span>
                      <span className="block text-sm font-medium">
                        {t(mode === 'redirect' ? 'SUPER_ADMIN.DEMO_SITES.MODE_REDIRECT' : 'SUPER_ADMIN.DEMO_SITES.MODE_EMBED')}
                      </span>
                      <span className="block text-sm text-muted-foreground">
                        {t(mode === 'redirect' ? 'SUPER_ADMIN.DEMO_SITES.MODE_REDIRECT_HINT' : 'SUPER_ADMIN.DEMO_SITES.MODE_EMBED_HINT')}
                      </span>
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </Field>
            </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={publishing}>
              {t('SUPER_ADMIN.DEMO_SITES.CANCEL')}
            </Button>
            <Button variant="outline" onClick={publish} disabled={!canPublish}>
              {publishing && <Loader2 data-icon="inline-start" className="animate-spin" />}
              {publishing ? t('SUPER_ADMIN.DEMO_SITES.PUBLISHING') : t('SUPER_ADMIN.DEMO_SITES.PUBLISH')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unpublish */}
      <Dialog open={!!unpublishing} onOpenChange={(o) => { if (!o && !removing) setUnpublishing(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('SUPER_ADMIN.DEMO_SITES.UNPUBLISH_TITLE', { url: unpublishing ? `${unpublishing.slug}.${DOMAIN}` : '' })}</DialogTitle>
            <DialogDescription>{t('SUPER_ADMIN.DEMO_SITES.UNPUBLISH_BODY')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnpublishing(null)} disabled={removing}>
              {t('SUPER_ADMIN.DEMO_SITES.CANCEL')}
            </Button>
            <Button
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={confirmUnpublish}
              disabled={removing}
            >
              {removing && <Loader2 data-icon="inline-start" className="animate-spin" />}
              {t('SUPER_ADMIN.DEMO_SITES.UNPUBLISH')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toast
        message={toast?.msg || ''}
        type={toast?.sev || 'success'}
        isVisible={!!toast}
        onClose={() => setToast(null)}
      />
    </PageContainer>
  );
};

export default DemoSitesPage;
